import { Element } from '@thive/canvas';
import { Rectangle, LogicalSpace, TrafficRule, VehicleModeExtension } from '@thive/fddm-types';
import { MathUtils, Vector2, Vector3 } from 'three';

import { theme } from '@/modules/common/theme';
import { centerOfSize } from '@/modules/common/helpers/math';
import { NumUtils } from '@/modules/common/utils/NumUtils';
import { FDDM_TAG, INTERACTIVITY_OFF } from '@/modules/floorplanDesign/constants';
import { assertNever } from '@/helpers/utils';
import { truncateWithEllipsis } from '@/helpers/string';
import {
  TRAFFIC_BOARD_EDGE_PADDING,
  TRAFFIC_BOARD_MAX_RADIUS,
  TRAFFIC_BOARD_MIN_RADIUS,
  TRAFFIC_BOARD_STACK_GAP,
  TRAFFIC_BOARD_RADIUS,
  TRAFFIC_BOARD_BLUE,
  TRAFFIC_BOARD_RED,
  TRAFFIC_BOARD_CROSS_THICKNESS,
  TRAFFIC_BOARD_CROSS_LENGTH,
  DO_NOT_STOP_CROSS_ANGLE,
  TRAFFIC_BOARD_STROKE_MULTIPLIER,
  VEHICLE_MODE_OFFSET,
  VEHICLE_MODE_LABEL_MAX_CHARS,
} from '@/modules/floorplanDesign/constants/trafficBoards';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';

import { calculateZoomInRange } from '@/modules/canvas/helpers';
import { elementFactory } from '@/modules/canvas/factories';
import { useCanvasStore } from '@/modules/canvas/store';
import { Z_INDEX_MAP } from '@/modules/canvas/constants/elements';

const TRAFFIC_BOARD_WIDTH_RATIO = 3;
const TRAFFIC_BOARD_HEIGHT_RATIO = 2;
const TRAFFIC_BOARD_TEXT_WIDTH_RATIO = 0.8;

const getCurrentCanvasZoomScale = (): number => {
  const canvas = useCanvasStore.getState().instance;
  if (!canvas) return 1;

  return calculateZoomInRange(
    canvas.getZoom(),
    TRAFFIC_BOARD_MIN_RADIUS,
    TRAFFIC_BOARD_MAX_RADIUS,
    TRAFFIC_BOARD_RADIUS,
    TRAFFIC_BOARD_RADIUS * 6,
  );
};

const getTrafficBoardLayout = (params: {
  parent: { width: number; height: number };
  count: number;
}): { center: Vector2; radius: number; gap: number } | null => {
  const { parent, count } = params;

  if (count <= 0) return null;
  if (parent.width <= 0 || parent.height <= 0) return null;

  const center = centerOfSize(parent);

  const padding = MathUtils.clamp(
    Math.min(parent.width, parent.height) / 4,
    0,
    TRAFFIC_BOARD_EDGE_PADDING,
  );

  const availableWidth = parent.width - 2 * padding;
  const availableHeight = parent.height - 2 * padding;

  if (availableWidth <= 0 || availableHeight <= 0) return null;

  const zoomScale = getCurrentCanvasZoomScale();
  let gap = TRAFFIC_BOARD_STACK_GAP;

  let maxBaseRadiusFromWidth =
    (availableWidth - (count - 1) * gap) / (count * TRAFFIC_BOARD_WIDTH_RATIO * zoomScale);

  if (maxBaseRadiusFromWidth <= 0) {
    gap = 0;
    maxBaseRadiusFromWidth = availableWidth / (count * TRAFFIC_BOARD_WIDTH_RATIO * zoomScale);
  }

  const baseRadius = Math.min(
    TRAFFIC_BOARD_MAX_RADIUS,
    availableHeight / (TRAFFIC_BOARD_HEIGHT_RATIO * zoomScale),
    maxBaseRadiusFromWidth,
  );

  if (baseRadius <= 0) return null;

  const radius = Math.max(TRAFFIC_BOARD_MIN_RADIUS, baseRadius) * zoomScale;

  return { center, radius, gap };
};

const createLogicalSpaceElement = (logicalSpace: LogicalSpace, rectangle: Rectangle): Element =>
  elementFactory.buildRectangleElement({
    id: logicalSpace.id,
    rectangle,
    z: Z_INDEX_MAP[FDDM_TAG.LOGICAL_SPACE],
    color: '#00000000',
    interactivity: {
      draggable: true,
      rotatable: true,
      selectable: true,
      scalable: true,
      snappable: true,
      clickable: true,
    },
    stroke: {
      color: theme.palette.neutral.dark,
      width: 30,
    },
    tags: [FDDM_TAG.LOGICAL_SPACE],
  });

const createTrafficBoardGroup = (
  logicalSpaceId: string,
  groupId: string,
  position: Vector2,
): Element =>
  elementFactory.buildNoneElement({
    id: groupId,
    parentId: logicalSpaceId,
    position: new Vector3(
      position.x,
      position.y,
      Z_INDEX_MAP[FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD],
    ),
    interactivity: INTERACTIVITY_OFF,
    tags: [FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD],
  });

const createDoNotStopBoardElement = (
  logicalSpaceId: string,
  position: Vector2,
  radius: number,
): Element[] => {
  const boardId = getBoardId(logicalSpaceId, 'DO_NOT_STOP');
  const groupId = `${boardId}-group`;

  return [
    createTrafficBoardGroup(logicalSpaceId, groupId, position),
    elementFactory.buildCircleElement({
      radius,
      id: boardId,
      position: {
        x: 0,
        y: 0,
        z: Z_INDEX_MAP[FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD] - 1,
      },
      color: TRAFFIC_BOARD_BLUE,
      tags: [FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD],
      parentId: groupId,
      stroke: {
        color: TRAFFIC_BOARD_RED,
        width: radius * TRAFFIC_BOARD_STROKE_MULTIPLIER,
      },
      interactivity: INTERACTIVITY_OFF,
    }),
    elementFactory.buildRectangleElement({
      id: `${boardId}-line-1`,
      rectangle: {
        angle: DO_NOT_STOP_CROSS_ANGLE,
        centerX: 0,
        centerY: 0,
        width: TRAFFIC_BOARD_CROSS_THICKNESS * radius,
        length: TRAFFIC_BOARD_CROSS_LENGTH * radius,
      },
      z: Z_INDEX_MAP[FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD],
      color: TRAFFIC_BOARD_RED,
      tags: [FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD],
      parentId: groupId,
      interactivity: INTERACTIVITY_OFF,
    }),
    elementFactory.buildRectangleElement({
      id: `${boardId}-line-2`,
      rectangle: {
        angle: -DO_NOT_STOP_CROSS_ANGLE,
        centerX: 0,
        centerY: 0,
        width: TRAFFIC_BOARD_CROSS_THICKNESS * radius,
        length: TRAFFIC_BOARD_CROSS_LENGTH * radius,
      },
      z: Z_INDEX_MAP[FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD],
      color: TRAFFIC_BOARD_RED,
      tags: [FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD],
      parentId: groupId,
      interactivity: INTERACTIVITY_OFF,
    }),
  ];
};

const createWordBoardElement = (
  groupId: string,
  text: string,
  width: number,
  offSetY: number = 0,
  idKey: string = text,
): Element => {
  const textId = `${groupId}-${idKey}`;

  return elementFactory.buildTextElement({
    id: textId,
    text,
    position: new Vector3(0, offSetY, Z_INDEX_MAP[FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD_TEXT]),
    fontSize: 300,
    width,
    wrapping: true,
    color: '#000000',
    horizontalAlignment: 'Center',
    verticalAlignment: 'Center',
    tags: [FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD_TEXT],
    parentId: groupId,
    name: `${textId}-name`,
    interactivity: INTERACTIVITY_OFF,
  });
};

const createRestrictedAccessBoard = (
  logicalSpaceId: string,
  position: Vector2,
  radius: number,
): Element[] => {
  const boardId = getBoardId(logicalSpaceId, 'RESTRICTED_ACCESS');
  const groupId = `${boardId}-group`;
  const offSetY = radius * 2;

  return [
    createTrafficBoardGroup(logicalSpaceId, groupId, position),
    elementFactory.buildCircleElement({
      radius,
      id: boardId,
      position: {
        x: 0,
        y: 0,
        z: Z_INDEX_MAP[FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD],
      },
      color: TRAFFIC_BOARD_RED,
      tags: [FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD],
      parentId: groupId,
      stroke: { color: TRAFFIC_BOARD_RED, width: Math.max(1, 50 * radius) },
      interactivity: INTERACTIVITY_OFF,
    }),
    elementFactory.buildRectangleElement({
      id: `${boardId}-rectangle`,
      rectangle: {
        angle: 0,
        centerX: 0,
        centerY: 0,
        width: radius * 0.3,
        length: radius * 1.5,
      },
      z: Z_INDEX_MAP[FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD] + 1,
      color: '#FFFFFF',
      tags: [FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD],
      parentId: boardId,
      interactivity: INTERACTIVITY_OFF,
    }),
    createWordBoardElement(groupId, 'Destination \n traffic only', 10, -offSetY),
  ];
};

const createSpeedLimitBoardElement = (
  logicalSpaceId: string,
  speed: number,
  position: Vector2,
  radius: number,
): Element[] => {
  const boardId = getBoardId(logicalSpaceId, 'SPEED_LIMIT');
  const groupId = `${boardId}-group`;
  const width = radius * TRAFFIC_BOARD_TEXT_WIDTH_RATIO;

  return [
    createTrafficBoardGroup(logicalSpaceId, groupId, position),
    elementFactory.buildCircleElement({
      radius,
      id: boardId,
      position: {
        x: 0,
        y: 0,
        z: Z_INDEX_MAP[FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD],
      },
      color: '#ffffff',
      tags: [FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD],
      parentId: groupId,
      stroke: {
        color: TRAFFIC_BOARD_RED,
        width: radius * TRAFFIC_BOARD_STROKE_MULTIPLIER,
      },
      interactivity: INTERACTIVITY_OFF,
    }),
    elementFactory.buildTextElement({
      id: `${boardId}-text-${speed}`,
      text: speed.toString(),
      position: new Vector3(0, 0, Z_INDEX_MAP[FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD_TEXT]),
      fontSize: 400,
      width,
      height: 550,
      wrapping: true,
      color: '#010000',
      horizontalAlignment: 'Center',
      verticalAlignment: 'Bottom',
      tags: [FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD_TEXT],
      parentId: boardId,
      name: 'speed-limit',
      interactivity: INTERACTIVITY_OFF,
    }),
  ];
};

const createVehicleModeBoardElement = (
  logicalSpaceId: string,
  position: Vector2,
  width: number,
  vehicleModeExtension: VehicleModeExtension,
): Element[] => {
  const { vehicleTypeId, vehicleModes } = vehicleModeExtension;
  const groupId = getBoardId(logicalSpaceId, 'VEHICLE_MODE', vehicleTypeId);

  const offset = width * VEHICLE_MODE_OFFSET;

  const vehicleModeElements = vehicleModes.map((mode, index) => {
    const offsetY = -(index + 1) * offset;

    return createWordBoardElement(
      groupId,
      truncateWithEllipsis(mode, VEHICLE_MODE_LABEL_MAX_CHARS),
      width,
      offsetY,
      mode,
    );
  });

  return [
    createTrafficBoardGroup(logicalSpaceId, groupId, position),
    createWordBoardElement(
      groupId,
      `${truncateWithEllipsis(vehicleTypeId, VEHICLE_MODE_LABEL_MAX_CHARS)}:`,
      width,
      0,
      `${vehicleTypeId}:`,
    ),
    ...vehicleModeElements,
  ];
};

const createTrafficRuleElements = (
  logicalSpaceId: string,
  rule: TrafficRule,
  position: Vector2,
  radius: number,
): Element[] => {
  switch (rule.type) {
    case 'DO_NOT_STOP':
      return createDoNotStopBoardElement(logicalSpaceId, position, radius);

    case 'RESTRICTED_ACCESS':
      return createRestrictedAccessBoard(logicalSpaceId, position, radius);

    case 'SPEED_LIMIT': {
      const maxSpeed = rule.speedLimitExtension?.maxSpeed;
      if (typeof maxSpeed !== 'number') return [];

      return createSpeedLimitBoardElement(logicalSpaceId, maxSpeed, position, radius);
    }

    case 'VEHICLE_MODE':
      if (!rule.vehicleModeExtension?.vehicleModes?.length) return [];

      return createVehicleModeBoardElement(
        logicalSpaceId,
        position,
        radius,
        rule.vehicleModeExtension,
      );

    default:
      assertNever(rule.type);
      return [];
  }
};

const isTrafficRuleRenderable = (rule: TrafficRule): boolean => {
  switch (rule.type) {
    case 'DO_NOT_STOP':
    case 'RESTRICTED_ACCESS':
      return true;

    case 'SPEED_LIMIT':
      return typeof rule.speedLimitExtension?.maxSpeed === 'number';

    case 'VEHICLE_MODE':
      return !!rule.vehicleModeExtension?.vehicleModes?.length;

    default:
      assertNever(rule.type);
      return false;
  }
};

export const createLogicalSpaceTrafficBoards = (
  logicalSpaceId: string,
  rules: TrafficRule[] | undefined,
  parent: { width: number; height: number },
): Element[] => {
  if (!rules?.length) return [];

  const parentInMeters = {
    width: NumUtils.mmToMeters(parent.width),
    height: NumUtils.mmToMeters(parent.height),
  };

  const layout = getTrafficBoardLayout({
    parent: parentInMeters,
    count: rules.length,
  });

  if (!layout) return [];

  const { center, radius, gap } = layout;

  const ruleWidth = TRAFFIC_BOARD_WIDTH_RATIO * radius;
  const totalWidth = ruleWidth * rules.length + gap * (rules.length - 1);
  const firstRuleCenterX = center.x - totalWidth / 2 + ruleWidth / 2;

  return rules.flatMap((rule, index) => {
    const horizontalPosition = new Vector2(firstRuleCenterX + index * (ruleWidth + gap), center.y);

    return createTrafficRuleElements(logicalSpaceId, rule, horizontalPosition, radius);
  });
};

/**
 * Map of all possible traffic rule types.
 * TypeScript will error if TrafficRule type adds a new type that isn't covered here.
 */
const RULE_TYPE_CONFIG = {
  DO_NOT_STOP: 'DO_NOT_STOP',
  RESTRICTED_ACCESS: 'RESTRICTED_ACCESS',
  SPEED_LIMIT: 'SPEED_LIMIT',
  VEHICLE_MODE: 'VEHICLE_MODE',
} satisfies Record<TrafficRule['type'], TrafficRule['type']>;

const ALL_RULE_TYPES: TrafficRule['type'][] = [
  RULE_TYPE_CONFIG.DO_NOT_STOP,
  RULE_TYPE_CONFIG.RESTRICTED_ACCESS,
  RULE_TYPE_CONFIG.SPEED_LIMIT,
  RULE_TYPE_CONFIG.VEHICLE_MODE,
];

/**
 * Generates a board ID from a logical space ID and rule type.
 * Ensures board IDs are consistent across all board creation functions.
 */
const getBoardId = (
  logicalSpaceId: string,
  ruleType: TrafficRule['type'],
  vehicleTypeId?: string,
): string => `${logicalSpaceId}-${ruleType}${vehicleTypeId ? `-${vehicleTypeId}` : ''}-board`;

/**
 * Returns IDs of traffic board elements that should be removed for a logical space.
 */
const getTrafficBoardElementIdsToRemove = (
  logicalSpaceId: string,
  rules: TrafficRule[] | undefined,
): string[] => {
  const currentRuleTypes = new Set(
    (rules ?? []).filter(isTrafficRuleRenderable).map((rule) => rule.type),
  );
  const idsToRemove: string[] = [];

  const canvas = useCanvasStore.getState().instance;
  const currentVehicleModeBoardIds = new Set(
    (rules ?? []).flatMap((rule) => {
      if (rule.type !== 'VEHICLE_MODE') return [];
      if (!isTrafficRuleRenderable(rule)) return [];

      const vehicleTypeId = rule.vehicleModeExtension?.vehicleTypeId;
      if (!vehicleTypeId) return [];

      return [getBoardId(logicalSpaceId, 'VEHICLE_MODE', vehicleTypeId)];
    }),
  );

  const existingVehicleModeBoardIds =
    canvas
      ?.getElementsByTag(new RegExp(FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD, 'i'))
      .filter(
        (element) =>
          element.parentId === logicalSpaceId &&
          element.id.includes(`${logicalSpaceId}-VEHICLE_MODE-`) &&
          element.id.endsWith('-board'),
      )
      .map((element) => element.id) ?? [];

  existingVehicleModeBoardIds.forEach((boardId) => {
    if (!currentVehicleModeBoardIds.has(boardId)) {
      idsToRemove.push(boardId);
    }
  });

  ALL_RULE_TYPES.forEach((ruleType) => {
    if (!currentRuleTypes.has(ruleType)) {
      if (ruleType === 'VEHICLE_MODE') {
        return;
      }

      const boardId = getBoardId(logicalSpaceId, ruleType);
      const groupId = `${boardId}-group`;

      idsToRemove.push(boardId, groupId);
    }
  });

  const existingTextIds =
    canvas
      ?.getElementsByTag(new RegExp(FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD_TEXT, 'i'))
      .filter((element) => element.parentId?.startsWith(`${logicalSpaceId}-`))
      .map((element) => element.id) ?? [];

  idsToRemove.push(...existingTextIds);

  return [...new Set(idsToRemove)];
};

export interface LogicalSpaceMapResult {
  elements: Element[];
  trafficBoardIdsToRemove: string[];
}

type LogicalSpaceMapItemResult = {
  elements: Element[];
  trafficBoardIdsToRemove: string[];
};

const mapLogicalSpace = (logicalSpace: LogicalSpace): LogicalSpaceMapItemResult => {
  const rectangle = RectangleUtils.polygonToRectangle(logicalSpace.polygon);
  if (!rectangle) {
    return {
      elements: [],
      trafficBoardIdsToRemove: [],
    };
  }

  const logicalSpaceElement = createLogicalSpaceElement(logicalSpace, {
    ...rectangle,
    angle: rectangle.angle,
  });

  const trafficBoardElements = createLogicalSpaceTrafficBoards(
    logicalSpace.id,
    logicalSpace.trafficRules,
    { width: rectangle.width, height: rectangle.length },
  );

  const trafficBoardIdsToRemove = getTrafficBoardElementIdsToRemove(
    logicalSpace.id,
    logicalSpace.trafficRules,
  );

  return {
    elements: [logicalSpaceElement, ...trafficBoardElements],
    trafficBoardIdsToRemove,
  };
};

export const logicalSpaceMapper = (logicalSpaces: LogicalSpace[]): LogicalSpaceMapResult => {
  const mappedLogicalSpaces = logicalSpaces.map(mapLogicalSpace);
  const elements = mappedLogicalSpaces.flatMap((mappedLogicalSpace) => mappedLogicalSpace.elements);
  const trafficBoardIdsToRemove = mappedLogicalSpaces.flatMap(
    (mappedLogicalSpace) => mappedLogicalSpace.trafficBoardIdsToRemove,
  );

  return { elements, trafficBoardIdsToRemove };
};
